import React, { useEffect, useRef, useState } from 'react';
import PdfCanvasViewer from './PdfCanvasViewer';
import './PdfPreviewReviewScreen.css';

export interface PdfPreviewReviewScreenProps {
  /** Screen heading (e.g. “Review PDF”) */
  screenTitle: string;
  /** Document name shown under the title */
  documentTitle: string;
  /** Optional short guidance under the document name */
  helperText?: string;
  pdfUrl: string | null;
  loading: boolean;
  error: boolean;
  loadingLabel: string;
  errorLabel: string;
  backLabel: string;
  confirmLabel: string;
  onBack: () => void;
  onConfirm: () => void;
  /** Shown when `pdfUrl` is set — opens the blob URL in a new tab */
  openInNewTabLabel?: string;
  onOpenInNewTab?: () => void;
  /** When set, show one PDF page at a time with prev/next navigation (1-based pages). */
  pagedTotalPages?: number;
  pageIndicatorLabel?: string;
  nextPageLabel?: string;
  prevPageLabel?: string;
  /**
   * When true with `pagedTotalPages`, each page is a separate PDF file (parent loads via `onPageChange`).
   * When false, a single multi-page PDF shows one page at a time via PDF.js.
   */
  pagedSeparateDocuments?: boolean;
  onPageChange?: (page: number) => void;
}

/**
 * Full-viewport PDF review after a fill-in chat: PDF.js canvas preview + back / continue.
 * Reusable for any flow that resolves a `blob:` URL (parent owns fetch + revoke).
 */
const PdfPreviewReviewScreen: React.FC<PdfPreviewReviewScreenProps> = ({
  screenTitle,
  documentTitle,
  helperText,
  pdfUrl,
  loading,
  error,
  loadingLabel,
  errorLabel,
  backLabel,
  confirmLabel,
  onBack,
  onConfirm,
  openInNewTabLabel,
  onOpenInNewTab,
  pagedTotalPages,
  pageIndicatorLabel,
  nextPageLabel,
  prevPageLabel,
  pagedSeparateDocuments,
  onPageChange,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const paged = typeof pagedTotalPages === 'number' && pagedTotalPages > 1;
  const skipPageChangeEffectRef = useRef(true);

  useEffect(() => {
    setCurrentPage(1);
    skipPageChangeEffectRef.current = true;
  }, [pagedTotalPages]);

  useEffect(() => {
    if (!paged || !pagedSeparateDocuments || !onPageChange) return;
    if (skipPageChangeEffectRef.current) {
      skipPageChangeEffectRef.current = false;
      return;
    }
    onPageChange(currentPage);
  }, [currentPage, paged, pagedSeparateDocuments, onPageChange]);

  const showPdf = Boolean(pdfUrl) && !loading && !error;
  const canOpenTab = Boolean(pdfUrl) && Boolean(onOpenInNewTab) && Boolean(openInNewTabLabel);
  const totalPages = paged ? pagedTotalPages! : 1;
  const onLastPage = !paged || currentPage >= totalPages;

  /** Page within the loaded PDF file (separate-document mode always uses page 1). */
  const pdfPageNumber = paged && !pagedSeparateDocuments ? currentPage : 1;

  const indicatorText =
    paged && pageIndicatorLabel
      ? pageIndicatorLabel
          .replace('{{current}}', String(currentPage))
          .replace('{{total}}', String(totalPages))
      : null;

  const handleSecondary = () => {
    if (paged && currentPage > 1 && prevPageLabel) {
      setCurrentPage((p) => Math.max(1, p - 1));
      return;
    }
    onBack();
  };

  const handlePrimary = () => {
    if (paged && !onLastPage && nextPageLabel) {
      setCurrentPage((p) => Math.min(totalPages, p + 1));
      return;
    }
    onConfirm();
  };

  const secondaryLabel =
    paged && currentPage > 1 && prevPageLabel ? prevPageLabel : backLabel;

  const primaryLabel = paged && !onLastPage && nextPageLabel ? nextPageLabel : confirmLabel;

  const inner = (
    <>
      <header className="pdf-preview-review__header">
        <h1 className="pdf-preview-review__title">{screenTitle}</h1>
        <p className="pdf-preview-review__doc-name">{documentTitle}</p>
        {indicatorText ? <p className="pdf-preview-review__page-indicator">{indicatorText}</p> : null}
        {helperText ? <p className="pdf-preview-review__helper">{helperText}</p> : null}
      </header>

      <div className="pdf-preview-review__main">
        {loading ? <div className="pdf-preview-review__state">{loadingLabel}</div> : null}
        {!loading && error ? (
          <div className="pdf-preview-review__state pdf-preview-review__state--error">{errorLabel}</div>
        ) : null}
        {showPdf && pdfUrl ? (
          <div className="pdf-preview-review__canvas-wrap">
            <PdfCanvasViewer
              key={pagedSeparateDocuments ? `${currentPage}-${pdfUrl}` : `${pdfUrl}-${pdfPageNumber}`}
              pdfUrl={pdfUrl}
              pageNumber={pdfPageNumber}
              title={documentTitle}
              renderErrorLabel={errorLabel}
            />
          </div>
        ) : null}
      </div>

      <footer className="pdf-preview-review__footer">
        <div className="pdf-preview-review__row">
          <button
            type="button"
            className="pdf-preview-review__btn pdf-preview-review__btn--secondary"
            onClick={handleSecondary}
          >
            {secondaryLabel}
          </button>
          <button
            type="button"
            className="pdf-preview-review__btn pdf-preview-review__btn--primary"
            onClick={handlePrimary}
            disabled={loading}
          >
            {primaryLabel}
          </button>
        </div>
        {canOpenTab ? (
          <button
            type="button"
            className="pdf-preview-review__link"
            disabled={!pdfUrl}
            onClick={() => pdfUrl && onOpenInNewTab?.()}
          >
            {openInNewTabLabel}
          </button>
        ) : null}
      </footer>
    </>
  );

  return (
    <div className="pdf-preview-review">
      <div className="pdf-preview-review__frame">{inner}</div>
    </div>
  );
};

export default PdfPreviewReviewScreen;
